import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/common/Header';
import { getApiUrl } from '../utils/apiConfig';
import { supabase } from '../utils/supabase';
import '../styles/BuddySelection.css';

const buddyOptions = [
  { id: 'Orbie', label: 'Orbie', image: process.env.PUBLIC_URL + '/Orbie.png' },
  { id: 'Itzy', label: 'Cat', image: process.env.PUBLIC_URL + '/ItzyWillReturn.png' }
];

const BuddySelection = () => {
  const [selectedBuddy, setSelectedBuddy] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();

  const handleBuddySelect = (buddy) => {
    setSelectedBuddy(buddy);
  };

  const handleContinue = async () => {
    // Prevent duplicate submissions
    if (loading || submitted) {
      console.log('⚠️ Buddy selection already submitted, ignoring duplicate click');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Get the user_id from localStorage (stored during profile setup).
      // Fall back to the authenticated Supabase session so this works when the
      // page is reached outside the fresh-signup flow (e.g. an existing login).
      let currentUserId = localStorage.getItem('currentUserId');
      if (!currentUserId) {
        const { data: { session } } = await supabase.auth.getSession();
        currentUserId = session?.user?.id || null;
        if (currentUserId) {
          localStorage.setItem('currentUserId', currentUserId);
          console.log('🔑 Recovered user_id from Supabase session:', currentUserId);
        }
      }
      console.log('🔑 Using user_id for buddy selection:', currentUserId);

      if (!currentUserId) {
        throw new Error('No user ID found. Please log in or complete profile setup first.');
      }

      // Send buddy selection data to backend
      console.log('🔄 Sending buddy selection to backend...');
      const response = await fetch(getApiUrl('buddy-selections'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: currentUserId,
          buddyType: selectedBuddy,
          username: localStorage.getItem('signupUsername') || 'Unknown'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 409) {
          // Buddy selection already exists, continue anyway
          console.log('⚠️ Buddy selection already exists, continuing to dashboard...');
          setSubmitted(true);
          localStorage.setItem('selectedBuddy', selectedBuddy);
          navigate('/student');
          return;
        }
        throw new Error(errorData.error || 'Failed to save buddy selection');
      }

      const result = await response.json();
      console.log('✅ Buddy selection saved to backend:', result);

      // Mark as submitted to prevent duplicate submissions
      setSubmitted(true);

      // Store in localStorage for later use
      localStorage.setItem('selectedBuddy', selectedBuddy);
      console.log('💾 Stored selectedBuddy in localStorage:', selectedBuddy);

      // Skip calibration — go straight to the dashboard
      console.log('🎯 Redirecting to dashboard...');
      navigate('/student');

    } catch (error) {
      console.error('Error saving buddy selection:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="buddy-selection-page">
      <Header buttons={[]} />
      <div className="buddy-selection-header">
        <p className="buddy-selection-header-text">Almost Done!</p><br/> 
        Follow the steps below so you can meet your <br />
        study buddy and measure your screen size using your super<br />
        eye powers, so the app works perfectly for you!
      </div>
      {error && (
        <div style={{ 
          color: 'red', 
          textAlign: 'center', 
          margin: '10px 0',
          padding: '10px',
          backgroundColor: '#ffe6e6',
          borderRadius: '5px'
        }}>
          {error}
        </div>
      )}
      <div className="buddy-selection-row">
        {/* Buddy Selection Box */}
        <div className="buddy-selection-container-left">
          <div className="buddy-selection-title">
            Click on a avatar you would like<br />
            to help you study!
          </div>
          <div className="buddy-selection-options">
            {buddyOptions.map((buddy) => (
              <div
                key={buddy.id}
                className={`buddy-selection-option${selectedBuddy === buddy.id ? ' selected' : ''}`}
                onClick={() => handleBuddySelect(buddy.id)}
              >
                {buddy.image ? (
                  <img src={buddy.image} alt={buddy.label} className="buddy-image" />
                ) : (
                  buddy.emoji
                )}
              </div>
            ))}
          </div>
          <button
            className="buddy-selection-continue-btn app-btn"
            onClick={handleContinue}
            disabled={!selectedBuddy || loading || submitted}
          >
            {submitted ? 'Buddy Selected!' : loading ? 'Saving...' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BuddySelection;